// src/components/WhataNewsLogin.js
// ═══════════════════════════════════════════════════════════════════════════
// VERSIÓN CORREGIDA
// ═══════════════════════════════════════════════════════════════════════════
//
// CAMBIOS:
//   - Redirige a la página original si venía de una ruta protegida
//   - Si ya está autenticado, redirige automáticamente a /whatanews-edit
//   - Mejor manejo de errores
//
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, User, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from './ThemeContext';
import './WhataNewsLogin.css';

const WhataNewsLogin = () => {
  const { theme } = useTheme();
  const { login, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Ruta desde la que venía el usuario (si existe); por defecto, el editor
  const from = location.state?.from?.pathname || '/whatanews-edit';

  // ═══════════════════════════════════════════════════════════════════════
  // REDIRECT SI YA ESTÁ AUTENTICADO
  // ═══════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!authLoading && isAuthenticated()) {
      console.log('[WhataNewsLogin] Ya autenticado, redirigiendo a:', from);
      navigate(from, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, from]);

  // ═══════════════════════════════════════════════════════════════════════
  // HANDLE SUBMIT
  // ═══════════════════════════════════════════════════════════════════════
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('[WhataNewsLogin] Intentando login...');

    try {
      const result = await login(username, password);

      if (result.success) {
        console.log('[WhataNewsLogin] Login exitoso, redirigiendo a:', from);
        navigate(from, { replace: true });
      } else {
        console.error('[WhataNewsLogin] Login fallido:', result.error);
        setError(result.error || 'Error al iniciar sesión');
      }
    } catch (err) {
      console.error('[WhataNewsLogin] Error:', err);
      setError('Error de conexión con el servidor');
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // LOADING STATE (mientras verifica autenticación inicial)
  // ═══════════════════════════════════════════════════════════════════════
  if (authLoading) {
    return (
      <div className="blog-login-page" data-theme={theme}>
        <div className="blog-login-container">
          <div className="blog-login-card">
            <div className="blog-login-loading">
              <div className="blog-login-spinner"></div>
              <p>Verificando sesión...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <div className="blog-login-page" data-theme={theme}>
      <div className="blog-login-container">
        <div className="blog-login-card">
          <div className="blog-login-header">
            <Lock size={48} className="blog-login-icon" />
            <h1>WhataNews</h1>
            <p>Editor Access</p>
          </div>

          <form className="blog-login-form" onSubmit={handleSubmit}>
            {error && (
              <div className="blog-login-error">
                <span>⚠️ {error}</span>
              </div>
            )}

            <div className="blog-login-field">
              <label htmlFor="username">
                <User size={18} />
                Usuario
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Ingresa tu usuario"
                required
                autoFocus
                disabled={loading}
              />
            </div>

            <div className="blog-login-field">
              <label htmlFor="password">
                <Lock size={18} />
                Contraseña
              </label>
              <div className="blog-login-password-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingresa tu contraseña"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="blog-login-toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="blog-login-submit"
              disabled={loading || !username || !password}
            >
              {loading ? (
                <>
                  <span className="blog-login-button-spinner"></span>
                  Ingresando...
                </>
              ) : (
                'Iniciar Sesión'
              )}
            </button>
          </form>

          <div className="blog-login-footer">
            <p>WhataLab © 2025</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhataNewsLogin;
